import { PublishStatus } from '@src/types.ts/publishRecord';
import { v4 as uuid } from 'uuid';

import type { PublishRecordRepository } from '@src/repositories';
import type {
  CreatePublishRecordInput,
  PublishFingerprints,
  PublishRecord,
  PublishSnapshotRef,
} from '@src/types.ts/publishRecord';

/**
 * One target of a publish command's fan-out — a `language × aspectRatio ×
 * resolution` cell rendering into a specific per-AR Video record. The caller
 * (fan-out orchestration) sets `mirrorsToLegacy` on the per-AR canonical cell
 * (original language × that AR × default resolution) so each AR's Video record
 * still gets its `videoFilePrimary` mirror; every other cell is record-only.
 */
export interface PublishCommandCell {
  videoId: string;
  language: string;
  aspectRatio: string;
  resolution: string;
  mirrorsToLegacy: boolean;
  /**
   * Builder-only: this cell renders the original language. Not persisted — the
   * record stores the raw `originalVideoLanguage` code in `language`, but the
   * source builder must receive the `ORIGINAL_LANGUAGE` sentinel so
   * `isOriginalLang`-based checks (karaoke no-op, composition short-circuit)
   * recognise it. Distinct from `mirrorsToLegacy`, which is true only on the
   * single highest-resolution canonical cell.
   */
  isOriginal: boolean;
  /**
   * Per-`(AR × language)` staleness fingerprint (PLAT-10023), computed from the
   * populated resources before creation so it persists atomically with the
   * record. `null`/absent when fingerprinting couldn't run for the cell.
   */
  fingerprints?: PublishFingerprints | null;
  /**
   * Provenance pointer map for this cell's **own AR** (`resourcesMetadata`).
   * Per-cell, not per-command: cells span multiple per-AR Video records, each
   * with its own resources, so a single command-level value would be wrong for
   * every non-main AR.
   */
  snapshotRef?: PublishSnapshotRef | null;
}

/**
 * Coordinates creation of `PublishRecord`s for a publish command. Single-target
 * publish uses `createCanonicalRecord`; the variant fan-out uses
 * `createRecordsForCommand` (N cells sharing one `groupId` / `snapshotRef`).
 *
 * Record creation is fatal — if we can't create the record, the publish must
 * not proceed (the backend needs `publishRecordId` to write status back).
 */
export class PublishRecordService {
  constructor(private readonly repository: PublishRecordRepository) {}

  /**
   * Create the canonical target record (original language × the video's AR ×
   * the chosen resolution) that dual-writes the legacy Video fields. Returns the
   * full new record (so the tracking store can cache it and merge lean
   * subscription pushes onto it). Throws if creation fails.
   */
  async createCanonicalRecord(input: {
    videoId: string;
    storyId: string;
    language: string;
    aspectRatio: string;
    resolution: string;
    snapshotRef?: PublishSnapshotRef | null;
    fingerprintsJson?: PublishFingerprints | null;
    publishedBy?: string | null;
  }): Promise<PublishRecord> {
    const recordInput: CreatePublishRecordInput = {
      videoId: input.videoId,
      storyId: input.storyId,
      groupId: uuid(),
      mirrorsToLegacy: true,
      language: input.language,
      aspectRatio: input.aspectRatio,
      resolution: input.resolution,
      publishStatus: PublishStatus.Queued,
      snapshotRef: input.snapshotRef ?? null,
      fingerprintsJson: input.fingerprintsJson ?? null,
      publishedBy: input.publishedBy ?? null,
      publishedAt: new Date().toISOString(),
    };
    const [created] = await this.repository.createRecords([recordInput]);
    if (!created) {
      throw new Error(
        'Failed to create canonical publish record: repository returned no item',
      );
    }
    return this.toRecord(created.id, recordInput);
  }

  /**
   * Create every record for a fan-out command in one shot — N cells sharing one
   * `groupId` / audit stamp. `mirrorsToLegacy`, `fingerprints`, and `snapshotRef`
   * are per cell (the caller derives each from that cell's own AR). Returns the
   * full records in cell order (the repository preserves input order) for
   * tracking + dispatch. Throws if any record fails to create.
   */
  async createRecordsForCommand(input: {
    storyId: string;
    cells: PublishCommandCell[];
    publishedBy?: string | null;
  }): Promise<PublishRecord[]> {
    if (input.cells.length === 0) return [];

    const groupId = uuid();
    const publishedAt = new Date().toISOString();
    const recordInputs: CreatePublishRecordInput[] = input.cells.map(cell => ({
      videoId: cell.videoId,
      storyId: input.storyId,
      groupId,
      mirrorsToLegacy: cell.mirrorsToLegacy,
      language: cell.language,
      aspectRatio: cell.aspectRatio,
      resolution: cell.resolution,
      publishStatus: PublishStatus.Queued,
      snapshotRef: cell.snapshotRef ?? null,
      fingerprintsJson: cell.fingerprints ?? null,
      publishedBy: input.publishedBy ?? null,
      publishedAt,
    }));

    const created = await this.repository.createRecords(recordInputs);
    if (created.length !== recordInputs.length) {
      throw new Error(
        `Failed to create publish records: expected ${recordInputs.length}, got ${created.length}`,
      );
    }
    return created.map((item, i) => this.toRecord(item.id, recordInputs[i]));
  }

  /**
   * Persist a record's terminal `failed` state. Used to reconcile a `queued`
   * record whose render was never dispatched (build/dispatch threw): writing
   * `failed` means the in-flight seed query won't resurrect it on a refresh.
   */
  async markFailed(recordId: string, errorMessage: string): Promise<void> {
    await this.repository.updateRecord(recordId, {
      publishStatus: PublishStatus.Failed,
      errorMessage,
    });
  }

  /**
   * In-flight records for one story (seed for live tracking on load/refresh).
   * Thin pass-through so store/component reads go through the service, not the
   * repository directly.
   */
  getInFlightForStory(storyId: string): Promise<PublishRecord[]> {
    return this.repository.getInFlightForStory(storyId);
  }

  /**
   * All publish records for a clip's Video record set (main + associated per-AR
   * ids), newest first. Thin pass-through so component reads go through the
   * service, not the repository directly.
   */
  getRecordsForVideos(videoIds: string[]): Promise<PublishRecord[]> {
    return this.repository.getRecordsForVideos(videoIds);
  }

  /** Build the full in-memory `PublishRecord` for a freshly-created item. */
  private toRecord(id: string, input: CreatePublishRecordInput): PublishRecord {
    return {
      id,
      videoId: input.videoId,
      storyId: input.storyId,
      groupId: input.groupId,
      mirrorsToLegacy: input.mirrorsToLegacy,
      language: input.language,
      aspectRatio: input.aspectRatio,
      resolution: input.resolution,
      publishStatus: input.publishStatus ?? PublishStatus.Queued,
      renderId: null,
      attempts: [],
      errorCode: null,
      errorMessage: null,
      renderStartedAt: null,
      renderCompletedAt: null,
      outputJson: null,
      snapshotRef: input.snapshotRef ?? null,
      fingerprintsJson: input.fingerprintsJson ?? null,
      publishedBy: input.publishedBy ?? null,
      publishedAt: input.publishedAt ?? null,
      renderSourceKey: input.renderSourceKey ?? null,
    };
  }
}
